/* eslint-disable complexity */

/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { AddressCard } from '@/components/blocks/AddressCard';
import { useCheckOut } from '@/data/Content/CheckOut';
import { useShipping } from '@/data/Content/Shipping';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { Address } from '@/data/types/Address';
import { ADDRESS_INIT, makeEditable, validateAddress } from '@/utils/address';
import { Alert, Box, Grid, MenuItem, Stack, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { ShippingCreateEditAddress } from '@/components/content/CheckOut/parts/Shipping/CreateEditAddress';
import { ChangeEvent } from 'react';
import { AddressSelectionSkeleton } from '@/components/content/CheckOut/parts/Shipping/AddressSelectionSkeleton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { checkoutDescriptionSX, checkoutTitleSX } from '@/components/content/CheckOut/styles/title';
import { combineSX } from '@/utils/combineSX';
import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import { DpOutlinedSelect } from '@/components/blocks/DpOutlineSelect';

export const isPhoneNumber = (value: string) => {
	const phoneRegex = /^\+?[1-9]\d{1,14}$/; // Simplified regex for international phone numbers
	return phoneRegex.test(value);
};

interface ShippingAddressSelectionProps {
	checkNotice?: boolean;
	onChangeInput?: (event: ChangeEvent<HTMLInputElement>) => void;
}

export const ShippingAddressSelection: React.FC<ShippingAddressSelectionProps> = () => {
	const shippingNLS = useLocalization('Shipping');
	const {
		availableAddress,
		selectedAddress,
		toggleEditCreateAddress,
		getCardActions,
		showError,
		isGuest,
		editableAddress,
		editablePersonalData,
		// toggleEditPersonalData,
		updateShippingInfo,
		orderItems,
		setSelectedItems,
		selectedItems,
		loading,
		selectedNickName,
		setSelectedNickName,
		primaryPersonalData,
	} = useContext(ContentContext) as ReturnType<typeof useCheckOut> & ReturnType<typeof useShipping>;
	const CustomIconComponent = (props: any) => (
		<KeyboardArrowDownIcon {...props} sx={{ color: '#011688' }} />
	);

	const canUsePersonal = useMemo(
		() => isGuest || availableAddress.some(({ primary }) => primary === 'true'),
		[availableAddress, isGuest]
	);

	useEffect(() => {
		if (primaryPersonalData && !primaryPersonalData?.zipCode) {
			toggleEditCreateAddress(makeEditable(primaryPersonalData as any))();
		}
	}, [primaryPersonalData, toggleEditCreateAddress]);

	const hasPrimaryAdd = Boolean(primaryPersonalData && !primaryPersonalData?.zipCode);

	const selectedAddressPersonalData = {
		email1: selectedAddress?.email1 ?? '',
		firstName: selectedAddress?.firstName ?? '',
		lastName: selectedAddress?.lastName ?? '',
		xaddr_lastNameMat: selectedAddress?.attributes?.[0]?.value ?? '',
		phone1: selectedAddress?.phone1 ?? '',
	};

	const filteredAddresses =
		selectedAddress && selectedAddress?.nickName
			? availableAddress.filter((address: any) => address.nickName === selectedNickName)
			: availableAddress;

	const handleSelectChange = useCallback(
		(event: SelectChangeEvent<any> | string) => {
			const newSelectedNickName =
				typeof event === 'string' ? event : (event.target.value as string);
			setSelectedNickName(newSelectedNickName as any);

			const selectedAddressData = availableAddress.find(
				(address) => address.nickName === newSelectedNickName
			);

			if (selectedAddressData) {
				const shippingInfo = {
					addressId: selectedAddressData.addressId,
					nickName: selectedAddressData.nickName,
				};
				updateShippingInfo(shippingInfo as any);
			}
		},
		[updateShippingInfo, availableAddress, setSelectedNickName]
	);

	useEffect(() => {
		if (availableAddress.length === 0 && selectedItems.length === 0 && !loading) {
			setSelectedItems([...orderItems]);
			return;
		}
	}, [availableAddress, orderItems, setSelectedItems, selectedItems.length, loading]);

	useEffect(() => {
		if (availableAddress.length > 0 && !selectedNickName && availableAddress[0].nickName) {
			handleSelectChange(availableAddress[0].nickName);
		}
	}, [availableAddress, handleSelectChange, selectedNickName]);

	if (loading || availableAddress.length === 0) {
		return <AddressSelectionSkeleton />;
	}

	return (
		<Stack spacing={{ xs: 2, sm: '9px' }} marginBottom={5} width="100%">
			<Grid>
				<Typography sx={checkoutTitleSX}>{'DATOS DE CONTACTO'}</Typography>
				<Typography sx={combineSX([checkoutDescriptionSX, { marginBottom: '7px' }])}>
					{'Entregaremos esta orden al usuario de esta cuenta'}
				</Typography>

				{!Boolean(editablePersonalData) ? (
					<Grid direction="column" container spacing={2} alignItems="stretch" mt="0px">
						{(filteredAddresses as Address[])?.map((address) => (
							<Grid paddingLeft={'var(--spacing-4)'} xs={12} key={address.nickName}>
								<AddressCard
									isCheckout={true}
									editPerson={true}
									personData={true}
									address={address}
									selectedAddressId={selectedAddress?.addressId ?? ''}
									actions={getCardActions(makeEditable(address), selectedAddress as Address)}
								/>
							</Grid>
						))}
					</Grid>
				) : (
					<ShippingCreateEditAddress editPerson={true} />
				)}
			</Grid>
			{availableAddress?.length > 0 ? (
				<Box mt="var(--font-size-micro)">
					{!editableAddress ? (
						<>
							<Typography sx={checkoutTitleSX}>{'DIRECCIÓN DE ENVÍO'}</Typography>
							<DpOutlinedSelect
								value={selectedNickName}
								displayEmpty
								renderValue={() =>
									isPhoneNumber(selectedNickName) ? 'Dirección principal' : selectedNickName
								}
								sx={{
									width: '100%',
									my: '0px',
									marginBottom: '0px',
									'&:hover .MuiOutlinedInput-notchedOutline': {
										borderColor: 'rgb(181, 190, 202)',
									},
								}}
								IconComponent={CustomIconComponent}
								onChange={handleSelectChange as any}
							>
								{availableAddress.map((address) => (
									<MenuItem key={address.nickName} value={address.nickName}>
										<Typography fontSize={'var(--font-size-body)'}>
											{isPhoneNumber(address?.nickName || '')
												? 'Dirección principal'
												: address.nickName}
										</Typography>
									</MenuItem>
								))}
							</DpOutlinedSelect>

							<Grid direction="column" container spacing={2} alignItems="stretch" my={2}>
								{(filteredAddresses as Address[])?.map((address) => (
									<Grid paddingLeft={'var(--spacing-4)'} xs={12} key={address.nickName}>
										<AddressCard
											isCheckout={true}
											address={address}
											selectedAddressId={selectedAddress?.addressId ?? ''}
											actions={getCardActions(makeEditable(address), selectedAddress as Address)}
										/>
									</Grid>
								))}
							</Grid>
						</>
					) : (
						<ShippingCreateEditAddress editPerson={false} hasPrimaryAdd={hasPrimaryAdd} />
					)}

					{showError && selectedAddress?.addressId && !validateAddress(selectedAddress) ? (
						<Alert variant="outlined" severity="error">
							{shippingNLS.Msgs.Incomplete.t()}
						</Alert>
					) : null}
					{canUsePersonal && !editableAddress && !editablePersonalData ? (
						<Box>
							<DpButton
								id="checkout-new-address-button"
								data-testid="checkout-new-address-button"
								variant="text"
								size="large"
								onClick={toggleEditCreateAddress({
									...ADDRESS_INIT,
									...selectedAddressPersonalData,
								})}
							>
								{'+ Agregar dirección nueva'}
							</DpButton>
						</Box>
					) : null}
				</Box>
			) : null}
			{showError && isEmpty(selectedAddress) ? (
				<Alert variant="outlined" severity="error">
					{shippingNLS.Msgs.SelectOrCreateAddress.t()}
				</Alert>
			) : null}
		</Stack>
	);
};
